import { supabase } from './supabaseClient';
import { Shop, RepairJob, Reminder, Staff, Service, Transaction, Discount, Rating, JobStatus, UserRole, Profile, ChatMessage, WorkTask } from '../types';

// Helper to validate UUID format before querying
const isValidUUID = (id: string) => {
  const regex = /^[0-9a-f]{8}-[0-9a-f]{4}-[4][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return regex.test(id);
};

export const db = {
  // Profiles
  async getProfile(userId: string): Promise<Profile | null> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      
      if (error) {
        if (error.code === 'PGRST116' || error.code === '42501') return null;
        throw error;
      }
      return data;
    } catch (err) {
      console.error("Error in getProfile:", err);
      return null;
    }
  },

  async getAllProfiles(): Promise<Profile[]> {
    const { data, error } = await supabase.from('profiles').select('*');
    if (error) throw error;
    return data || [];
  },

  async createProfile(profile: Partial<Profile>) {
    const { data, error } = await supabase
      .from('profiles')
      .insert([{
        id: profile.id,
        role: profile.role,
        full_name: profile.full_name,
        email: profile.email,
        avatar_url: profile.avatar_url
      }])
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  // Shops
  async getShops(): Promise<Shop[]> {
    const { data, error } = await supabase.from('shops').select('*');
    if (error) throw error;
    return (data || []).map(s => ({
      id: s.id,
      owner_id: s.owner_id,
      name: s.name,
      category: s.categories || [],
      rating: s.rating,
      address: s.address,
      imageUrl: s.image_url,
      logoUrl: s.logo_url,
      description: s.description,
      contact: { 
        phone: s.phone, 
        telegram: s.telegram,
        email: s.email 
      },
      workingHours: s.working_hours,
      coordinates: { lat: s.lat, lng: s.lng },
      vatPercentage: s.vat_percentage || 0
    }));
  },

  async createShop(shop: Partial<Shop>) {
    const { data, error } = await supabase.from('shops').insert({
      owner_id: shop.owner_id,
      name: shop.name,
      categories: shop.category,
      address: shop.address,
      description: shop.description,
      phone: shop.contact?.phone,
      working_hours: shop.workingHours,
      lat: shop.coordinates?.lat,
      lng: shop.coordinates?.lng,
      image_url: shop.imageUrl,
      logo_url: shop.logoUrl,
      rating: 5.0,
      vat_percentage: 0
    }).select().single();
    
    if (error) throw error;
    return data;
  },

  async updateShop(id: string, updates: Partial<Shop>) {
    const dbUpdates: any = {};
    if (updates.name) dbUpdates.name = updates.name;
    if (updates.category) dbUpdates.categories = updates.category;
    if (updates.address) dbUpdates.address = updates.address;
    if (updates.description) dbUpdates.description = updates.description;
    if (updates.contact?.phone) dbUpdates.phone = updates.contact.phone;
    if (updates.contact?.telegram) dbUpdates.telegram = updates.contact.telegram;
    if (updates.workingHours) dbUpdates.working_hours = updates.workingHours;
    if (updates.coordinates?.lat) dbUpdates.lat = updates.coordinates.lat;
    if (updates.coordinates?.lng) dbUpdates.lng = updates.coordinates.lng;
    if (updates.vatPercentage !== undefined) dbUpdates.vat_percentage = updates.vatPercentage;
    if (updates.imageUrl) dbUpdates.image_url = updates.imageUrl;
    if (updates.logoUrl) dbUpdates.logo_url = updates.logoUrl;

    const { data, error } = await supabase.from('shops').update(dbUpdates).eq('id', id).select();
    if (error) throw error;
    return data[0];
  },

  // Repair Jobs
  async getRepairJobs(userId: string, role: UserRole): Promise<RepairJob[]> {
    // Joining profiles to get the customer's full name dynamically
    let query = supabase.from('repair_jobs').select('*, shops(name), profiles:customer_id(full_name)');
    
    if (role === UserRole.CUSTOMER) {
      query = query.eq('customer_id', userId);
    } else if (role === UserRole.SHOP_OWNER) {
      const { data: ownedShops } = await supabase.from('shops').select('id').eq('owner_id', userId);
      const shopIds = (ownedShops || []).map(s => s.id);
      if (shopIds.length > 0) {
        query = query.in('shop_id', shopIds);
      } else {
        return [];
      }
    } else if (role === UserRole.STAFF) {
      const { data: user } = await supabase.auth.getUser();
      const userEmail = user.user?.email;
      if (!userEmail) return [];

      const { data: staffRecord } = await supabase
        .from('staff')
        .select('shop_id')
        .eq('email', userEmail)
        .maybeSingle();

      if (staffRecord?.shop_id) {
        query = query.eq('shop_id', staffRecord.shop_id);
      } else {
        return [];
      }
    }

    const { data, error } = await query.order('created_at', { ascending: false });
    if (error) throw error;

    return (data || []).map(j => ({
      id: j.id,
      shopId: j.shop_id,
      shopName: j.shops?.name || 'Unknown Shop',
      customerId: j.customer_id,
      customerName: j.profiles?.full_name || 'Client',
      description: j.description,
      status: j.status as JobStatus,
      createdAt: j.created_at,
      contactMethod: j.contact_method as any,
      contactValue: j.contact_value,
      preferredSlots: j.preferred_slots || [],
      scheduledDate: j.scheduled_date,
      estimatedCost: j.estimated_cost,
      aiDiagnosis: j.ai_diagnosis,
      assignedStaffIds: j.assigned_staff_ids || [],
      workTasks: j.work_tasks || [],
      invoice: j.invoice,
      paymentProof: j.payment_proof,
      customerLocation: { lat: j.lat, lng: j.lng, map_url: j.map_url },
      messages: j.messages || [],
      isRated: j.is_rated || false
    }));
  },

  async createRepairJob(job: any) {
    const diagnosis = job.aiDiagnosis || job.diagnosis;
    const location = job.customerLocation || job.location;
    const slots = job.preferredSlots || job.preferred_slots;

    // Removed customer_name from insert to match actual schema
    const { data, error } = await supabase.from('repair_jobs').insert({
      shop_id: job.shopId,
      customer_id: job.customerId,
      description: job.description,
      contact_method: job.contactMethod,
      contact_value: job.contactValue,
      preferred_slots: slots,
      ai_diagnosis: diagnosis,
      estimated_cost: diagnosis?.estimatedPriceRange || job.estimatedCost,
      lat: location?.lat,
      lng: location?.lng,
      map_url: location?.mapUrl,
      status: JobStatus.PENDING
    }).select();
    
    if (error) throw error;
    return data[0];
  },

  async updateJob(jobId: string, updates: Partial<RepairJob>) {
    const dbUpdates: any = {};
    if (updates.status) dbUpdates.status = updates.status;
    if (updates.scheduledDate) dbUpdates.scheduled_date = updates.scheduledDate;
    if (updates.assignedStaffIds) dbUpdates.assigned_staff_ids = updates.assignedStaffIds;
    if (updates.workTasks) dbUpdates.work_tasks = updates.workTasks;
    if (updates.invoice) dbUpdates.invoice = updates.invoice;
    if (updates.paymentProof) dbUpdates.payment_proof = updates.paymentProof;
    if (updates.isRated !== undefined) dbUpdates.is_rated = updates.isRated;
    if (updates.messages) dbUpdates.messages = updates.messages;

    const { data, error } = await supabase.from('repair_jobs').update(dbUpdates).eq('id', jobId).select();
    if (error) throw error;
    return data[0];
  },

  async updateJobStatus(jobId: string, status: JobStatus) {
    const { error } = await supabase.from('repair_jobs').update({ status }).eq('id', jobId);
    if (error) throw error;
  },

  // Reminders
  async getReminders(userId: string): Promise<Reminder[]> {
    const { data, error } = await supabase.from('reminders').select('*').eq('customer_id', userId);
    if (error) throw error;
    return (data || []).map(r => ({
      id: r.id,
      customerId: r.customer_id,
      title: r.title,
      description: r.description,
      type: r.type,
      dueDate: r.due_date,
      remindAt: r.due_date,
      isCompleted: r.is_completed,
      category: r.category
    }));
  },

  async saveReminders(rems: Partial<Reminder>[]) {
    const { data, error } = await supabase.from('reminders').insert(rems.map(r => ({
      customer_id: r.customerId,
      title: r.title,
      description: r.description,
      type: r.type,
      due_date: r.dueDate,
      is_completed: r.isCompleted,
      category: r.category
    }))).select();
    if (error) throw error;
    return data;
  },

  async deleteReminder(id: string) {
    const { error } = await supabase.from('reminders').delete().eq('id', id);
    if (error) throw error;
  },

  async toggleReminder(id: string, isCompleted: boolean) {
    const { error } = await supabase.from('reminders').update({ is_completed: isCompleted }).eq('id', id);
    if (error) throw error;
  },

  // Team / Staff
  async getStaff(shopId: string): Promise<Staff[]> {
    if (!isValidUUID(shopId)) return [];
    const { data, error } = await supabase.from('staff').select('*').eq('shop_id', shopId);
    if (error) throw error;
    return (data || []).map(s => ({
      id: s.id,
      name: s.name,
      email: s.email,
      role: s.role,
      skills: s.skills || [],
      shopId: s.shop_id,
      avatarUrl: s.avatar_url
    }));
  },

  async createStaff(staff: Partial<Staff>) {
    const { data, error } = await supabase.from('staff').insert({
      shop_id: staff.shopId,
      name: staff.name,
      email: staff.email,
      role: staff.role,
      skills: staff.skills,
      avatar_url: staff.avatarUrl
    }).select().single();
    if (error) throw error;
    return data;
  },

  async deleteStaff(id: string) {
    const { error } = await supabase.from('staff').delete().eq('id', id);
    if (error) throw error;
  },

  // Services
  async getServices(shopId: string): Promise<Service[]> {
    if (!isValidUUID(shopId)) return [];
    const { data, error } = await supabase.from('services').select('*').eq('shop_id', shopId);
    if (error) throw error;
    return (data || []).map(s => ({
      id: s.id,
      shopId: s.shop_id,
      name: s.name,
      category: s.category,
      description: s.description,
      imageUrl: s.image_url,
      status: s.status,
      items: s.items || []
    }));
  },

  async createService(service: Partial<Service>) {
    const { data, error } = await supabase.from('services').insert({
      shop_id: service.shopId,
      name: service.name,
      category: service.category,
      description: service.description,
      image_url: service.imageUrl,
      status: service.status,
      items: service.items
    }).select().single();
    if (error) throw error;
    return data;
  },

  async updateService(id: string, service: Partial<Service>) {
    const { data, error } = await supabase.from('services').update({
      name: service.name,
      category: service.category,
      description: service.description,
      image_url: service.imageUrl,
      status: service.status,
      items: service.items
    }).eq('id', id).select().single();
    if (error) throw error;
    return data;
  },

  async deleteService(id: string) {
    const { error } = await supabase.from('services').delete().eq('id', id);
    if (error) throw error;
  },

  // Transactions / Finance
  async getTransactions(shopId: string): Promise<Transaction[]> {
    if (!isValidUUID(shopId)) return [];
    const { data, error } = await supabase.from('transactions').select('*').eq('shop_id', shopId);
    if (error) throw error;
    return (data || []).map(t => ({
      id: t.id,
      shopId: t.shop_id,
      type: t.type,
      category: t.category,
      amount: t.amount,
      description: t.description,
      date: t.date
    }));
  },

  async createTransaction(t: Partial<Transaction>) {
    const { data, error } = await supabase.from('transactions').insert({
      shop_id: t.shopId,
      type: t.type,
      category: t.category,
      amount: t.amount,
      description: t.description,
      date: t.date || new Date().toISOString()
    }).select().single();
    if (error) throw error;
    return data;
  },

  // Discounts
  async getDiscounts(shopId: string): Promise<Discount[]> {
    if (!isValidUUID(shopId)) return [];
    const { data, error } = await supabase.from('discounts').select('*').eq('shop_id', shopId);
    if (error) throw error;
    return (data || []).map(d => ({
      id: d.id,
      shopId: d.shop_id,
      name: d.name,
      description: d.description,
      type: d.type,
      valueType: d.value_type,
      value: d.value,
      startDate: d.start_date,
      endDate: d.end_date,
      minGroupSize: d.min_group_size,
      targetCategories: d.target_categories,
      minBookings: d.min_bookings,
      status: d.status
    }));
  },

  async createDiscount(d: Partial<Discount>) {
    const { data, error } = await supabase.from('discounts').insert({
      shop_id: d.shopId,
      name: d.name,
      description: d.description,
      type: d.type,
      value_type: d.valueType,
      value: d.value,
      start_date: d.startDate,
      end_date: d.endDate,
      min_group_size: d.minGroupSize,
      target_categories: d.targetCategories,
      min_bookings: d.minBookings,
      status: d.status
    }).select().single();
    if (error) throw error;
    return data;
  },

  async deleteDiscount(id: string) {
    const { error } = await supabase.from('discounts').delete().eq('id', id);
    if (error) throw error;
  },

  async toggleDiscount(id: string, currentStatus: string) {
    const nextStatus = currentStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    const { error } = await supabase.from('discounts').update({ status: nextStatus }).eq('id', id);
    if (error) throw error;
    return nextStatus;
  },

  // Ratings
  async getRatings(shopId: string): Promise<Rating[]> {
    if (!isValidUUID(shopId)) return [];
    const { data, error } = await supabase.from('ratings').select('*').eq('shop_id', shopId);
    if (error) throw error;
    return (data || []).map(r => ({
      id: r.id,
      shopId: r.shop_id,
      jobId: r.job_id,
      customerId: r.customer_id,
      customerName: r.customer_name,
      stars: r.stars,
      comment: r.comment,
      createdAt: r.created_at
    }));
  },

  async createRating(rating: Partial<Rating>) {
    const { data, error } = await supabase.from('ratings').insert({
      shop_id: rating.shopId,
      job_id: rating.jobId,
      customer_id: rating.customerId,
      customer_name: rating.customerName,
      stars: rating.stars,
      comment: rating.comment
    }).select().single();
    if (error) throw error;
    
    if (rating.jobId) {
      await supabase.from('repair_jobs').update({ is_rated: true }).eq('id', rating.jobId);
    }
    
    return data;
  }
};